import classNames from 'classnames';
import React, { Dispatch, SetStateAction, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ProButton } from '../../../../atoms/Button/ProButton';
import { ProIconButton } from '../../../../atoms/Button/ProIconButton';
import { ProIconData, ProIconDataButtons } from '../../../../atoms/Icons/IconData';
import { ProjectStatus } from '../../../../atoms/ProjectStatus/ProjectStatus';
import { ProTable, TableColData } from '../../../../atoms/Table/Table';
import { ProValue } from '../../../../atoms/Value/ProValue';
import { ApplicationElementType, getBaseBCItemForType } from '../../../../components/DetailWindow/breadCrump.helpers';
import { ProPage } from '../../../../components/Page/Page';
import { WBSNameCell } from '../../../../components/WBSTable/WBSNameCell';
import { ProjectContext } from '../../../../contexts/ProjectContext';
import { WBSContext } from '../../../../contexts/WBSContext';
import { ProNoDataOrRightsPage } from '../../../../modules/ProErrorPage/NoDataOrRights';
import { useWBSDetailWindow } from '../../../../modules/Windows/WBSElement/WBSElementHook/WBSDetailFrame';
import { ProIntent, ProSize } from '../../../../utils/enums';
import { getHiddenForWindowDisplayState } from '../../../../utils/helpers';
import { Components } from '../../../../utils/types';
import { usePlanningFilter } from '../Planning/hooks/usePlanningFilter';

export const ReviewSprintSelectionPage = ({
    setSelectedSprint,
}: {
    setSelectedSprint: Dispatch<SetStateAction<Components.Schemas.WBSElementDeltaDTO | undefined>>;
}) => {
    const { t } = useTranslation();
    const { selectedProject, loading, selectedTeam } = useContext(ProjectContext);

    const {
        selectedWBSStructure,
        selectedWBSElement,
        setSelectedWBSElement,
        filteredData,
        openNodes,
        deselectWBSElement,
        selectedTableRow,
        setSelectedTableRow,
        toggleOpenNode,
        syncWBSScructure,
        wbsStructureError,
        wbsStructureLoading,
    } = useContext(WBSContext);

    const {
        open,
        renderWBSDetailWindow,
        isDisplayed,
        mode: detailWindowMode,
    } = useWBSDetailWindow('Sprint', deselectWBSElement, selectedWBSElement, syncWBSScructure);

    const { render: ReqFilterAndSearch, filteredData: refilteredData } = usePlanningFilter(filteredData);

    const selectTableRow = (id: number) => {
        if (selectedWBSStructure) {
            setSelectedWBSElement(filteredData![id]);
            open('view');
            setSelectedTableRow(id);
        }
    };

    const tableData: TableColData[] = [
        {
            name: t('table.cols.wbsName'),
            cellRender: (i) => (
                <WBSNameCell
                    baseData={selectedWBSStructure}
                    displayData={filteredData}
                    i={i}
                    openNodes={openNodes}
                    toggleOpenNode={toggleOpenNode}
                />
            ),
            className: 'TableRowName ProTable__levels',
            colSpace: 'extend',
        },
        {
            name: t('table.cols.action'),
            cellRender: (i) => (
                <div className="ProTable--actionCell">
                    {(filteredData[i].wbsElementType === 'Sprint' ||
                        filteredData[i].wbsElementType === 'WorkPackage') && (
                        <ProButton
                            text={'Start review'}
                            mainIntent={ProIntent.Success}
                            onClick={() => setSelectedSprint(filteredData[i])}
                        />
                    )}
                </div>
            ),
            className: `ProTable__buttons`,
            colSpace: 4,
        },
        {
            name: t('table.cols.status'),
            cellRender: (i) => <ProjectStatus delta={filteredData[i]} id={filteredData[i]?.id} />,
            className: 'ProTable__status',
            colSpace: 3,
        },
        {
            name: t('table.cols.responsible'),
            cellRender: (i) =>
                filteredData![i]?.responsiblePerson
                    ? `${filteredData![i]?.responsiblePerson?.firstName ?? ''} ${
                          filteredData![i]?.responsiblePerson?.lastName ?? 'no data'
                      }`
                    : '',
            hidden: () => !isDisplayed,
            colSpace: 4,
        },
        {
            name: t('table.cols.complete'),
            cellRender: (i) => <ProValue type="value" entity="%" value={filteredData![i]?.degreeOfCompletion} />,
            hidden: () => !isDisplayed,
            colSpace: 3,
            rightAligned: true,
        },

        {
            name: t('table.cols.constraint'),
            cellRender: (i) => filteredData![i]?.constraintQualityType,
            hidden: () => !isDisplayed,
            colSpace: 4,
        },
        {
            name: t('table.cols.action'),
            cellRender: (i) => (
                <div className={classNames('ProTable--actionCell')}>
                    <ProIconButton
                        icon={ProIconDataButtons.arrow_right_xs}
                        tooltip={t('buttons.open')}
                        mainIntent={ProIntent.Transparent}
                        hoverIntent={ProIntent.Secondary}
                        size={ProSize.XSmall}
                        onClick={() => selectTableRow(i)}
                        lineIntent={ProIntent.Secondary}
                        disabled={getHiddenForWindowDisplayState(detailWindowMode)}
                        placement="bottom"
                    />
                </div>
            ),
            className: `ProTable__buttons`,
            colSpace: 2,
        },
    ];

    if (!loading && (!selectedTeam || !selectedProject)) {
        return <ProNoDataOrRightsPage message={[t('error.noData.noTeam'), t('error.noData.noPortfolio')]} />;
    }
    return (
        <ProPage
            breadCrump={getBaseBCItemForType(
                [
                    ApplicationElementType.TeamManagement,
                    { type: ApplicationElementType.TeamManagement, name: selectedTeam?.name },
                ],
                t
            )}
            name={'Please select a sprint for review'}
            error={wbsStructureError}
            loading={wbsStructureLoading}
            icon={ProIconData.quality_fill}
            displyWBSSelector
            displayProjectSelector
            filter={<ReqFilterAndSearch />}
        >
            {selectedWBSStructure && tableData && (
                <ProTable
                    data={tableData}
                    rowsCount={filteredData?.length ?? 0}
                    selectedTableRow={selectedTableRow}
                    getRowClass={(i) => `ProTable--row__level-${filteredData![i]?.level ?? 0}`}
                />
            )}
            <div
                className={classNames(
                    'ProApp--detailWindowSection',
                    isDisplayed && 'ProApp--detailWindowSection__expanded'
                )}
            >
                {renderWBSDetailWindow()}
            </div>
        </ProPage>
    );
};
